import type {
  ActiveTenantsCountResponse,
  CreateAlertResponse,
  FindTenantsRequest,
  FindTenantsResponse,
  GetMyFavoriteTenantsResponse,
  ToggleFavoriteTenantRequest,
} from '../models/index';
import * as runtime from '../runtime';

export interface FindTenantsOperationRequest {
  findTenantsRequest: FindTenantsRequest;
}

export interface GetActiveTenantsCountRequest {
  cityName?: string;
}

export interface ToggleFavoriteRequest {
  toggleFavoriteTenantRequest: ToggleFavoriteTenantRequest;
}

/**
 *
 */
export class TenantApi extends runtime.BaseAPI {
  /**
   */
  async findTenantsRaw(
    requestParameters: FindTenantsOperationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<FindTenantsResponse>> {
    if (requestParameters.findTenantsRequest == null) {
      throw new runtime.RequiredError(
        'findTenantsRequest',
        'Required parameter "findTenantsRequest" was null or undefined when calling findTenants().',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/tenant/findtenants`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.findTenantsRequest,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Find tenants based on a set of filters
   */
  async findTenants(
    requestParameters: FindTenantsOperationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<FindTenantsResponse> {
    const response = await this.findTenantsRaw(
      requestParameters,
      initOverrides,
    );
    return response.value();
  }

  /**
   */
  async getActiveTenantsCountRaw(
    requestParameters: GetActiveTenantsCountRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<ActiveTenantsCountResponse>> {
    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    if (requestParameters.cityName != null) {
      queryParameters.cityName = requestParameters.cityName;
    }

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/tenant/activetenantscount`,
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Active tenants counts for a given city name. if no city name is provided it will return the active tenants count for the Netherlands
   */
  async getActiveTenantsCount(
    requestParameters: GetActiveTenantsCountRequest = {},
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<ActiveTenantsCountResponse> {
    const response = await this.getActiveTenantsCountRaw(
      requestParameters,
      initOverrides,
    );
    return response.value();
  }

  /**
   */
  async getMyFavoriteTenantsRaw(
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<GetMyFavoriteTenantsResponse>> {
    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/tenant/my-favorite-tenants`,
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Get my favorite tenants
   */
  async getMyFavoriteTenants(
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<GetMyFavoriteTenantsResponse> {
    const response = await this.getMyFavoriteTenantsRaw(initOverrides);
    return response.value();
  }

  /**
   */
  async toggleFavoriteRaw(
    requestParameters: ToggleFavoriteRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<CreateAlertResponse>> {
    if (requestParameters.toggleFavoriteTenantRequest == null) {
      throw new runtime.RequiredError(
        'toggleFavoriteTenantRequest',
        'Required parameter "toggleFavoriteTenantRequest" was null or undefined when calling toggleFavorite().',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/tenant/toggle-favorite`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.toggleFavoriteTenantRequest,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Toggle favorite flag for a given tenant ID
   */
  async toggleFavorite(
    requestParameters: ToggleFavoriteRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<CreateAlertResponse> {
    const response = await this.toggleFavoriteRaw(
      requestParameters,
      initOverrides,
    );
    return response.value();
  }
}
